import { useAuth } from '@/hooks/use-auth';
import {
  deleteListStaple as deleteListStapleQuery,
  insertListStaple,
  type ListStapleRow,
} from '@/lib/queries/list-staples';
import {
  getCachedListStaples,
  invalidateListStaplesCache,
  invalidateListStaplesForList,
  setCachedListStaples,
  type ListStaple,
} from '@/lib/list-staples-cache';
import { supabase } from '@/lib/supabase';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

function rowToListStaple(row: ListStapleRow): ListStaple {
  return {
    id: row.id,
    list_id: row.list_id,
    user_id: row.user_id,
    item_name: row.item_name,
    category: row.category ?? 'other',
    is_custom: row.is_custom ?? true,
    times_purchased: row.times_purchased ?? 0,
    created_at: row.created_at,
  };
}

/**
 * Fetch the current user's staples for a given list.
 * Uses cache keyed by (userId, listId). When listId is null/empty, returns empty.
 */
export function useListStaples(listId: string | null) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [listStaples, setListStaples] = useState<ListStaple[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(
    async (forceNetwork = false) => {
      if (!user?.id || !listId) {
        setListStaples([]);
        setLoading(false);
        return;
      }

      const cached = getCachedListStaples(userId, listId);
      if (cached !== null && !forceNetwork) {
        setListStaples(cached);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('list_staples')
          .select('*')
          .eq('list_id', listId)
          .eq('user_id', user.id)
          .order('item_name', { ascending: true });

        if (fetchError) throw fetchError;
        const result = (data ?? []).map((r) => rowToListStaple(r as ListStapleRow));
        setListStaples(result);
        setCachedListStaples(user.id, listId, result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    },
    [user?.id, userId, listId]
  );

  useLayoutEffect(() => {
    if (!listId || !userId) {
      setListStaples([]);
      setLoading(false);
      return;
    }
    const cached = getCachedListStaples(userId, listId);
    if (cached !== null) {
      setListStaples(cached);
      setLoading(false);
    }
  }, [userId, listId]);

  useEffect(() => {
    if (!user?.id || !listId) {
      setListStaples([]);
      setLoading(false);
      return;
    }
    refetch();
  }, [user?.id, listId, refetch]);

  return { listStaples, loading, error, refetch };
}

/**
 * Mutation: add a list staple (current user, given list).
 */
export function useAddListStaple(listId: string | null) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const addListStaple = useCallback(
    async (itemName: string, category = 'Other'): Promise<ListStaple | null> => {
      const trimmed = itemName.trim();
      if (!trimmed || !user?.id || !listId) {
        setError(new Error('You must be signed in and have a list selected to add staples'));
        return null;
      }

      setLoading(true);
      setError(null);
      try {
        const { data, error: insertError } = await insertListStaple({
          list_id: listId,
          user_id: user.id,
          item_name: trimmed,
          category,
          is_custom: true,
        });

        if (insertError) throw insertError;
        invalidateListStaplesForList(user.id, listId);
        return data ? rowToListStaple(data as ListStapleRow) : null;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [user?.id, listId]
  );

  return { addListStaple, loading, error };
}

/**
 * Mutation: remove a list staple. Pass listId to invalidate cache for that list.
 */
export function useDeleteListStaple(listId: string | null) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteListStaple = useCallback(
    async (stapleId: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const { error: deleteError } = await deleteListStapleQuery(stapleId);

        if (deleteError) throw deleteError;
        if (user?.id && listId) {
          invalidateListStaplesForList(user.id, listId);
        } else {
          invalidateListStaplesCache();
        }
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [user?.id, listId]
  );

  return { deleteListStaple, loading, error };
}
